import { f7, useStore } from 'framework7-react';
import { FC, useEffect, useRef, useState } from 'react';
import {
  getSubscriptionStatusApi,
  postSubscriptionRegisterVehicleApi,
} from '@/api/subscription';
import { PageTemplate } from '@/components/uiParts/Template/PageTemplate/PageTemplate';
import { initialState } from '@/config/initialState';
import { paths } from '@/config/paths';
import { store } from '@/config/store';
import { Car } from '@/types/api/customerApi';
import { storeDispatch } from '@/utils/store';

interface SubscriptionCompletePageProps {
  subscriptionId?: number;
  planName?: string;
  monthlyFee?: number;
  hasRegisteredCar?: boolean;
}

export const SubscriptionCompletePage: FC<SubscriptionCompletePageProps> = ({
  subscriptionId,
  planName: propPlanName,
  monthlyFee,
  hasRegisteredCar: propHasRegisteredCar,
}) => {
  const authInfo = store.state.authInfo as any;
  const ownedCars = useStore(store, 'carsInfo') as Car[];
  const [hasRegisteredCar, setHasRegisteredCar] = useState(
    propHasRegisteredCar || false,
  );
  const [hasPaymentDone, setHasPaymentDone] = useState(false);
  const [planName, setPlanName] = useState(propPlanName || '');

  // 初回レンダリング時の車両数を記録。車検証登録で増えた場合のみ自動紐付けを実行する
  const initialCarCountRef = useRef<number | null>(null);
  if (initialCarCountRef.current === null) {
    initialCarCountRef.current = ownedCars?.length ?? 0;
  }

  useEffect(() => {
    const fetchAndLink = async () => {
      try {
        const res = await getSubscriptionStatusApi(authInfo.m_customer_id);
        if (res.data.success && res.data.data?.subscription) {
          const sub = res.data.data.subscription as any;
          if (!propPlanName && sub.plan_name) setPlanName(sub.plan_name);

          // apply_status: 1=支払い待ち, 2=初回決済済み（継続課金未登録）, 0=有効化完了
          // 0のときのみ決済完了（Step2含む全ステップ完了）
          if (sub.apply_status === 0) {
            setHasPaymentDone(true);
          }

          // 既に紐付け済み
          if (sub.t_stock_car_id) {
            setHasRegisteredCar(true);
            return;
          }

          // 車検証登録後の自動紐付け:
          // 初回表示時の車両数より増えている = 車検証登録で新しい車両ができた場合のみ実行
          // 初回表示時に既存車両を紐付けてしまう問題を防止
          const currentCarCount = ownedCars?.length ?? 0;
          const carsAdded = currentCarCount > (initialCarCountRef.current ?? 0);
          if (sub.status === 2 && carsAdded && ownedCars.length > 0) {
            const latestCar = ownedCars[ownedCars.length - 1];
            const stockCarId = (latestCar as any).t_stock_car_id;
            if (stockCarId && sub.t_subscription_id) {
              try {
                await postSubscriptionRegisterVehicleApi({
                  t_subscription_id: sub.t_subscription_id,
                  t_stock_car_id: stockCarId,
                });
                setHasRegisteredCar(true);
              } catch (linkErr) {
                console.error('車両紐付けエラー', linkErr);
              }
            }
          }
        }
      } catch (e) {
        console.error(e);
      }
    };
    if (authInfo?.m_customer_id) fetchAndLink();
  }, [authInfo?.m_customer_id, ownedCars?.length]);
  const handleClickPayment = () => {
    f7.view.main.router.navigate(paths.subscriptionPayment, {
      props: {
        subscriptionId,
        planName,
        monthlyFee,
      },
    });
  };

  const handleClickHome = () => {
    f7.view.main.router.navigate(paths.home);
  };

  return (
    <PageTemplate
      pageName="subscription-complete"
      title={planName || 'サブスクリプション'}
      showNavBack
    >
      <div style={{ padding: '32px 16px', textAlign: 'center' }}>
        {/* 完了バッジ */}
        <div
          style={{
            display: 'inline-block',
            padding: '6px 16px',
            backgroundColor: '#E3F2FD',
            borderRadius: '20px',
            fontSize: '13px',
            color: '#1E55E6',
            fontWeight: 'bold',
            marginBottom: '16px',
          }}
        >
          お申し込み完了！
        </div>

        <div
          style={{
            width: '48px',
            height: '48px',
            margin: '0 auto 16px',
            borderRadius: '50%',
            backgroundColor: '#E8F5E9',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '24px',
            color: '#4CAF50',
          }}
        >
          ✓
        </div>

        <h2
          style={{ fontSize: '18px', fontWeight: 'bold', marginBottom: '24px' }}
        >
          {planName || 'サブスクリプションサービス'}の<br />
          お申し込みありがとうございます！
        </h2>

        {/* ステータス */}
        <div
          style={{
            padding: '20px',
            margin: '0 0 24px',
            backgroundColor: '#fff',
            borderRadius: '12px',
            border: '1px solid #e0e0e0',
            textAlign: 'left',
          }}
        >
          <div
            style={{ fontSize: '13px', color: '#666', marginBottom: '12px' }}
          >
            サービス開始にはお支払いと車両の本登録が必要です
          </div>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              padding: '10px 0',
              borderBottom: '1px solid #f0f0f0',
            }}
          >
            <span style={{ color: '#4CAF50', fontSize: '16px' }}>✓</span>
            <span style={{ fontSize: '14px' }}>お申し込み</span>
            <span
              style={{
                marginLeft: 'auto',
                fontSize: '12px',
                color: '#4CAF50',
                fontWeight: 'bold',
              }}
            >
              完了
            </span>
          </div>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              padding: '10px 0',
              borderBottom: '1px solid #f0f0f0',
            }}
          >
            {hasPaymentDone ? (
              <>
                <span style={{ color: '#4CAF50', fontSize: '16px' }}>✓</span>
                <span style={{ fontSize: '14px' }}>お支払い</span>
                <span
                  style={{
                    marginLeft: 'auto',
                    fontSize: '12px',
                    color: '#4CAF50',
                    fontWeight: 'bold',
                  }}
                >
                  完了
                </span>
              </>
            ) : (
              <>
                <span style={{ color: '#FF9800', fontSize: '16px' }}>○</span>
                <span style={{ fontSize: '14px' }}>お支払い</span>
                <span
                  style={{
                    marginLeft: 'auto',
                    fontSize: '11px',
                    padding: '2px 8px',
                    borderRadius: '4px',
                    backgroundColor: '#FFF3E0',
                    color: '#E65100',
                  }}
                >
                  未完了
                </span>
              </>
            )}
          </div>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              padding: '10px 0',
            }}
          >
            {hasRegisteredCar ? (
              <>
                <span style={{ color: '#4CAF50', fontSize: '16px' }}>✓</span>
                <span style={{ fontSize: '14px' }}>車両の本登録</span>
                <span
                  style={{
                    marginLeft: 'auto',
                    fontSize: '12px',
                    color: '#4CAF50',
                    fontWeight: 'bold',
                  }}
                >
                  完了
                </span>
              </>
            ) : (
              <>
                <span style={{ color: '#FF9800', fontSize: '16px' }}>○</span>
                <span style={{ fontSize: '14px' }}>車両の本登録</span>
                <span
                  style={{
                    marginLeft: 'auto',
                    fontSize: '11px',
                    padding: '2px 8px',
                    borderRadius: '4px',
                    backgroundColor: '#FFF3E0',
                    color: '#E65100',
                  }}
                >
                  未完了
                </span>
              </>
            )}
          </div>
        </div>

        {/* 決済へ（支払い未完了時のみ表示） */}
        {!hasPaymentDone && (
          <button
            onClick={handleClickPayment}
            style={{
              width: '100%',
              padding: '16px',
              marginBottom: '12px',
              fontSize: '16px',
              fontWeight: 'bold',
              color: '#fff',
              backgroundColor: '#1E55E6',
              border: 'none',
              borderRadius: '28px',
              cursor: 'pointer',
            }}
          >
            お支払いへ進む
          </button>
        )}

        {!hasRegisteredCar && (
          <button
            onClick={() => {
              const affiliateId = (store.state as any).customerInfo
                ?.m_affiliate_id;
              storeDispatch('setRegisterUser', 'registerUser', {
                ...initialState.registerUser,
                affiliate_id: Number(affiliateId),
                currentStep: 3,
              });
              f7.view.main.router.navigate(paths.registerNewMember, {
                props: { sourcePage: paths.subscriptionComplete, startStep: 3 },
              });
            }}
            style={{
              width: '100%',
              padding: '16px',
              marginBottom: '12px',
              fontSize: '14px',
              fontWeight: 'bold',
              color: '#1E55E6',
              backgroundColor: '#fff',
              border: '1px solid #1E55E6',
              borderRadius: '28px',
              cursor: 'pointer',
            }}
          >
            車検証を登録する
          </button>
        )}

        <button
          onClick={handleClickHome}
          style={{
            width: '100%',
            padding: '16px',
            fontSize: '14px',
            color: '#666',
            backgroundColor: '#fff',
            border: '1px solid #ccc',
            borderRadius: '28px',
            cursor: 'pointer',
          }}
        >
          あとで手続きする
        </button>
      </div>
    </PageTemplate>
  );
};
